'use client';

import { Language } from '@whoop/i18n';
import {
  useRouter,
  usePathname,
  useSearchParams,
  useParams,
} from 'next/navigation';
import useLocale from '@/hooks/useLocale';
import type { CountrySelection } from 'ui/types/CountrySelectorTypes';
import { getRouteForCountryAndLanguage } from '@whoop/i18n/utils/routingHelpers';
import { useCartStore } from '@/stores/StoreProvider';

const cleanPath = (path: string) => path.replace(/^\/+|\/+$/g, '');

export function createLocalizedPathGenerator({
  country,
  language,
}: {
  country: string;
  language: Language;
}) {
  return (path: string): string => {
    return `/${country}/${language}/${cleanPath(path)}`;
  };
}

export function useNavigation() {
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const clearCart = useCartStore((state) => state.clearCart);

  const getLocalizedPath = createLocalizedPathGenerator({
    country: locale.country,
    language: locale.language,
  });

  const goTo = (path: string) => {
    router.push(getLocalizedPath(path));
  };

  const changeSiteCountryAndLanguage = ({
    countryCode,
    languageCode,
  }: CountrySelection) => {
    // No redirect/refresh required if member clicks on the current country/language again
    const currentCountry = params.country;
    const currentLanguage = params.language;
    const newCountry = countryCode.toLowerCase();
    const newLanguage = languageCode.toLowerCase() as Language;
    // No redirect/refresh required if member clicks on the current country/language again
    if (newCountry === currentCountry && newLanguage === currentLanguage) {
      return;
    }
    // redirect to new path, keep any search params
    const queryParams = new URLSearchParams(searchParams).toString();
    const segments = pathname.split('/');
    let path = `/${segments.slice(3).join('/')}`;
    if (queryParams) {
      path += `?${queryParams}`;
    }
    const newPath = getRouteForCountryAndLanguage({
      country: newCountry,
      language: newLanguage,
      path: path,
    });
    // if country was switched, empty cart
    if (newCountry !== currentCountry) {
      clearCart();
    }
    router.push(newPath);
  };

  return {
    getLocalizedPath,
    goTo,
    changeSiteCountryAndLanguage,
  };
}
